import * as signalR from '@microsoft/signalr'
import { config } from '@/config'

class SignalRService {
    constructor() {
        this.connection = null
        this.isConnected = false
        this.isConnecting = false
        this.eventBus = null
        this.logger = null
        this.auth = null
        this.baseUrl = config.apiUrl
        this.reconnectAttempts = 0
        this.maxReconnectAttempts = 5
        this.reconnectDelays = [0, 2000, 5000, 10000, 30000]
    }

    /**
     * Initialize the service with dependencies
     */
    initialize(eventBus, logger, auth) {
        this.eventBus = eventBus
        this.logger = logger
        this.auth = auth
    }

    /**
     * Get current authentication token
     */
    async getAuthToken() {
        try {
            const token = await this.auth.getAccessTokenSilently()
            return token
        } catch (error) {
            this.logger.error('Failed to get auth token for SignalR:', error)
            throw error
        }
    }

    /**
     * Create and configure the SignalR connection
     */
    async createConnection() {
        try {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(`${this.baseUrl}/hubs/queries`, {
                    accessTokenFactory: () => this.getAuthToken(),
                    transport: signalR.HttpTransportType.LongPolling,
                })
                .withHubProtocol(new signalR.JsonHubProtocol())
                .withServerTimeout(60000)
                .withKeepAliveInterval(10000)
                .withAutomaticReconnect(this.reconnectDelays)
                .withStatefulReconnect(true)
                .configureLogging(signalR.LogLevel.Information)
                .build()

            this.setupEventHandlers()
            return this.connection
        } catch (error) {
            this.logger.error('Failed to create SignalR connection:', error)
            throw error
        }
    }

    /**
     * Set up event handlers for the SignalR connection
     */
    setupEventHandlers() {
        if (!this.connection) return

        // Query completion events
        this.connection.on('QueryCompleted', (payload) => {
            this.eventBus.emit('signalr-query-completed', {
                ...payload,
                timestamp: new Date().toISOString(),
            })
        })

        this.connection.on('QueryError', (payload) => {
            this.eventBus.emit('signalr-query-error', {
                ...payload,
                timestamp: new Date().toISOString(),
            })
        })

        // Connection lifecycle events
        this.connection.onclose((error) => {
            this.isConnected = false
            this.eventBus.emit('signalr-connection-closed', { error })
        })

        this.connection.onreconnecting((error) => {
            this.isConnected = false
            this.reconnectAttempts++
            this.eventBus.emit('signalr-reconnecting', {
                attempt: this.reconnectAttempts,
                error,
            })
        })

        this.connection.onreconnected((connectionId) => {
            this.isConnected = true
            this.reconnectAttempts = 0
            this.eventBus.emit('signalr-reconnected', { connectionId })
        })
    }

    /**
     * Connect to the SignalR hub
     */
    async connect() {
        if (this.isConnected || this.isConnecting) {
            return this.connection
        }

        try {
            this.isConnecting = true

            if (!this.connection) {
                await this.createConnection()
            }

            await this.connection.start()
            this.isConnected = true
            this.isConnecting = false
            this.reconnectAttempts = 0

            this.logger.info('SignalR connected successfully')
            this.eventBus.emit('signalr-connected', {
                connectionId: this.connection.connectionId,
            })

            return this.connection
        } catch (error) {
            this.isConnecting = false
            this.logger.error('SignalR connection failed:', error)
            this.eventBus.emit('signalr-connection-failed', { error })
            throw error
        }
    }

    /**
     * Disconnect from the SignalR hub
     */
    async disconnect() {
        if (!this.connection) return

        try {
            await this.connection.stop()
            this.isConnected = false
            this.connection = null
            this.logger.info('SignalR disconnected')
            this.eventBus.emit('signalr-disconnected')
        } catch (error) {
            this.logger.error('Error disconnecting SignalR:', error)
        }
    }

    /**
     * Check if connected
     */
    getConnectionState() {
        return {
            isConnected: this.isConnected,
            isConnecting: this.isConnecting,
            connectionId: this.connection?.connectionId,
            state: this.connection?.state,
        }
    }

    /**
     * Ensure connection is established
     */
    async ensureConnection() {
        if (!this.isConnected && !this.isConnecting) {
            await this.connect()
        }
        return this.connection
    }

    /**
     * Add custom event listener
     */
    on(eventName, handler) {
        if (this.connection) {
            this.connection.on(eventName, handler)
        }
    }

    /**
     * Remove event listener
     */
    off(eventName, handler) {
        if (this.connection) {
            this.connection.off(eventName, handler)
        }
    }

    /**
     * Invoke server method
     */
    async invoke(methodName, ...args) {
        await this.ensureConnection()
        return this.connection.invoke(methodName, ...args)
    }
}

// Create singleton instance
const signalRService = new SignalRService()

export default signalRService
